/**
 * Internal dependencies
 */
import { domReady } from './dom-ready';
import type { PreloadQueryArgUrl, Session } from '../../types';
import { addNabQueryArg } from '../url';

export const preloadQueryArgs = ( session: Session ): void =>
	domReady( () => {
		doPreloadQueryArgs( session );
		fixSelfHashUrls();
	} );

// =======
// HELPERS
// =======

export const doPreloadQueryArgs = ( session: Session ): void => {
	if ( ! session.preloadQueryArgUrls.length ) {
		return;
	}

	// eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
	const matches = document.cookie.match( /nabAlternative=[0-9]+/ ) || [ '' ];
	const alt: number = Number.parseInt( matches[ 0 ].split( '=' )[ 1 ] ?? '' );
	if ( isNaN( alt ) ) {
		return;
	}

	const site = document.location.protocol + '//' + document.location.hostname;
	const anchors = Array.from(
		document.querySelectorAll< HTMLAnchorElement >( 'a' )
	)
		.filter( ( a ) => a.href )
		.filter( ( a ) => typeof a.href === 'string' )
		.filter( ( a ) => 0 === a.href.indexOf( site ) )
		.filter( ( a ) => -1 === a.href.indexOf( '#' ) )
		.filter( ( a ) => -1 === a.href.indexOf( '/wp-content/' ) );

	const config = [ ...session.preloadQueryArgUrls ].sort( ( x ) =>
		x.type === 'alt-urls' ? -1 : 1
	);
	anchors.forEach( preloadQueryArg( config, alt, session.nabPosition ) );
};

const fixSelfHashUrls = () => {
	const current = new URL( document.location.href );
	if ( ! /\bnab=/.test( current.search ) ) {
		return;
	}

	current.hash = '';
	current.searchParams.delete( 'nab' );
	Array.from(
		document.querySelectorAll< HTMLAnchorElement >( 'a[href*="#"]' )
	)
		.filter( ( a ) => a.href )
		.filter( ( a ) => typeof a.href === 'string' )
		.filter( ( a ) => a.href.replace( /#.*$/, '' ) === current.href )
		.forEach( ( a ) => {
			a.href = a.href.replace( /^[^#]*#/, '#' );
		} );
};

const preloadQueryArg =
	(
		config: ReadonlyArray< PreloadQueryArgUrl >,
		alt: number,
		nabPosition: 'first' | 'last'
	) =>
	( a: HTMLAnchorElement ): void => {
		if ( ! a.href || typeof a.href !== 'string' ) {
			return;
		}

		if ( a.href.indexOf( 'nab=' ) !== -1 ) {
			return;
		}

		const scopes = getMatchingScopes( config, a.href );
		if ( ! scopes.length ) {
			return;
		}
		const altCount = Math.max( ...scopes.map( ( s ) => s.altCount ) );
		// TODO DAVID. This preloading is invalid… (and it has always been, I think).
		const nab = alt % altCount;
		const url = getAlternativeUrl( scopes, alt ) || a.href;
		a.href = addNabQueryArg( url, nab, nabPosition );
	};

function getMatchingScopes(
	config: ReadonlyArray< PreloadQueryArgUrl >,
	link: string
): ReadonlyArray< PreloadQueryArgUrl > {
	link = link.toLowerCase();
	return config.filter( ( s ) => {
		const scope = s.type === 'alt-urls' ? s.altUrls : s.scope;
		return scope.some( ( l ): boolean => {
			if ( isPartialUrl( l ) ) {
				return link.includes( getPart( l ) );
			}
			if ( isPartialNotIncludedUrl( l ) ) {
				return ! link.includes( getPart( l ) );
			}
			if ( isExclusionUrl( l ) ) {
				return l.substring( 1 ) !== link;
			}
			return link === l;
		} );
	} );
}

function getAlternativeUrl(
	config: ReadonlyArray< PreloadQueryArgUrl >,
	nab: number
) {
	const scope = config.find( ( s ) => s.type === 'alt-urls' );
	return scope?.type === 'alt-urls'
		? scope.altUrls[ nab % scope.altCount ]
		: undefined;
}

function isExclusionUrl( url: string ): boolean {
	return url.startsWith( '!' ) && ! url.startsWith( '!*' );
}

function isPartialUrl( url: string ): boolean {
	return url.startsWith( '*' ) && url.endsWith( '*' );
}

function isPartialNotIncludedUrl( url: string ): boolean {
	return url.startsWith( '!*' ) && url.endsWith( '*' );
}

function getPart( partialUrl: string ): string {
	return partialUrl.substring(
		// We either remove '!*' or '*' at the beginning
		isPartialNotIncludedUrl( partialUrl ) ? 2 : 1,
		// And we remove the final '*' at the end
		partialUrl.length - 1
	);
}
